import { Link } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';

export default function TermsPage() {
  const { t } = useLanguage();
  const back = t('legal.backToHome');

  return (
    <main className="legal-page">
      <article className="legal-prose">
        <p className="legal-prose__notice">{t('terms.notice')}</p>

        <h1>Terms &amp; Conditions</h1>
        <p className="legal-prose__meta">
          <strong>Terms &amp; Conditions – Maalin iyo Habeen</strong>
        </p>
        <p className="legal-prose__meta">
          <strong>Last Updated:</strong> July 2026
        </p>

        <hr className="legal-prose__rule" />

        <h2>1. Introduction</h2>
        <p>
          Welcome to Maalin iyo Habeen (&ldquo;we&rdquo;, &ldquo;us&rdquo;,
          &ldquo;our&rdquo;, or the &ldquo;Organization&rdquo;). These Terms
          &amp; Conditions (&ldquo;Terms&rdquo;) govern your use of our website,
          donation platform, and participation in our fundraising campaigns,
          including the National Capital Project Fund initiative.
        </p>
        <p>
          By accessing our website, making a donation, or participating in any
          of our programs, you agree to be bound by these Terms. If you do not
          agree, please do not use our services.
        </p>

        <hr className="legal-prose__rule" />

        <h2>2. Definitions</h2>
        <ul>
          <li>
            <strong>&ldquo;Organization&rdquo;</strong> – Maalin iyo Habeen, a
            registered charitable organization in the Republic of Somaliland.
          </li>
          <li>
            <strong>&ldquo;Donor&rdquo;</strong> – Any individual or entity that
            contributes funds to the Organization.
          </li>
          <li>
            <strong>&ldquo;Investor&rdquo;</strong> – A Donor whose contribution
            is allocated to the 60% National Capital Project Fund.
          </li>
          <li>
            <strong>&ldquo;Charitable Funds&rdquo;</strong> – The 40% portion of
            proceeds allocated to humanitarian relief.
          </li>
          <li>
            <strong>&ldquo;Capital Funds&rdquo;</strong> – The 60% portion of
            proceeds allocated to the National Capital Project Fund.
          </li>
          <li>
            <strong>&ldquo;Committee&rdquo;</strong> – The independent body of
            selected elders and experts responsible for overseeing the Capital
            Fund escrow account.
          </li>
          <li>
            <strong>&ldquo;Escrow Account&rdquo;</strong> – A secure,
            independently monitored bank account holding Capital Funds pending
            allocation.
          </li>
          <li>
            <strong>&ldquo;Voting Member&rdquo;</strong> – Any individual
            registered to participate in the democratic allocation of Capital
            Funds.
          </li>
        </ul>

        <hr className="legal-prose__rule" />

        <h2>3. Eligibility</h2>
        <p>
          <strong>3.1</strong> You must be at least 18 years of age to make a
          donation or participate as an Investor or Voting Member.
        </p>
        <p>
          <strong>3.2</strong> By donating, you confirm that the funds you
          provide are legally obtained and not derived from any unlawful
          activity.
        </p>
        <p>
          <strong>3.3</strong> The Organization reserves the right to refuse or
          return any donation at its sole discretion, without liability.
        </p>

        <hr className="legal-prose__rule" />

        <h2>4. Donations &amp; Fund Allocation</h2>

        <h3>4.1 Donation Split</h3>
        <p>All proceeds from fundraising campaigns shall be distributed as follows:</p>
        <ul>
          <li>
            <strong>40% – Charitable Relief:</strong>
            <ul>
              <li>
                Prioritized for the most needy communities within Somaliland.
              </li>
              <li>
                A 5% administrative fee is deducted from this portion to cover
                operational costs (staff, transportation, fuel, vehicle
                maintenance, office rent, and supplies).
              </li>
              <li>
                The remaining 95% is applied directly to charitable programs as
                described on our website.
              </li>
            </ul>
          </li>
          <li>
            <strong>60% – National Capital Project Fund:</strong>
            <ul>
              <li>Deposited into an independently managed Escrow Account.</li>
              <li>
                Allocated through a democratic voting process as described in
                Section 5.
              </li>
              <li>
                Maalin iyo Habeen has no decision-making authority over how these
                funds are spent.
              </li>
            </ul>
          </li>
        </ul>

        <h3>4.2 Donation Methods</h3>
        <p>Donations may be made via:</p>
        <ul>
          <li>Zaad</li>
          <li>Edahab</li>
          <li>Bank transfer</li>
          <li>Any other method specified on our website</li>
        </ul>

        <h3>4.3 Refunds &amp; Cancellations</h3>
        <p>
          <strong>4.3.1</strong> Donations are generally non-refundable once
          processed.
        </p>
        <p>
          <strong>4.3.2</strong> Refunds may be considered in exceptional
          circumstances, such as:
        </p>
        <ul>
          <li>Duplicate or erroneous transactions</li>
          <li>Technical errors by the Organization</li>
          <li>Fraudulent use of your payment method</li>
        </ul>
        <p>
          <strong>4.3.3</strong> Refund requests must be submitted in writing
          within 7 days of the transaction, providing proof of payment. Refunds
          are at the Organization&rsquo;s sole discretion and may be subject to
          administrative deductions.
        </p>
        <p>
          <strong>4.3.4</strong> Once Capital Funds are released from Escrow, no
          refund is possible.
        </p>

        <hr className="legal-prose__rule" />

        <h2>5. National Capital Project Fund – Voting &amp; Governance</h2>

        <h3>5.1 Voting Rights</h3>
        <p>
          <strong>5.1.1</strong> All funds in the Capital Fund Escrow Account
          shall be allocated through a democratic voting process open to:
        </p>
        <ul>
          <li>Members of the public (non-investors)</li>
          <li>Investors (donors who have contributed to the Capital Fund)</li>
        </ul>
        <p>
          <strong>5.1.2</strong> Each eligible voter receives one base vote.
        </p>
        <p>
          <strong>5.1.3</strong> Investors receive 1/3 additional voting power
          (i.e., 1.33 votes per investor) for each verified contribution to the
          Capital Fund. This weighting may be adjusted by the Committee to ensure
          proportionality.
        </p>
        <p>
          <strong>5.1.4</strong> The Organization does not hold any voting rights
          over Capital Fund allocation.
        </p>

        <h3>5.2 The Committee</h3>
        <p>
          <strong>5.2.1</strong> An independent Committee of selected elders and
          experts is appointed to oversee the Escrow Account.
        </p>
        <p>
          <strong>5.2.2</strong> The Committee&rsquo;s responsibilities include:
        </p>
        <ul>
          <li>Validating proposed projects</li>
          <li>Ensuring compliance with agreed criteria</li>
          <li>Releasing funds to approved projects</li>
          <li>
            Returning funds to investors if projects fail or timetables are not
            met
          </li>
          <li>Approving or rejecting allocation proposals</li>
          <li>Monitoring the overall integrity of the process</li>
        </ul>
        <p>
          <strong>5.2.3</strong> All Committee decisions must be made by majority
          vote and recorded in writing.
        </p>
        <p>
          <strong>5.2.4</strong> The Committee members are not affiliated with
          Maalin iyo Habeen and act independently.
        </p>

        <h3>5.3 Escrow Account</h3>
        <p>
          <strong>5.3.1</strong> All Capital Funds are held in a secure,
          independently monitored Escrow Account.
        </p>
        <p>
          <strong>5.3.2</strong> Funds may only be released upon:
        </p>
        <ul>
          <li>A valid vote approving the allocation</li>
          <li>Approval by the Committee</li>
          <li>Compliance with all relevant laws and regulations</li>
        </ul>
        <p>
          <strong>5.3.3</strong> The Escrow Account is subject to regular audits
          by independent third-party auditors, with results published annually.
        </p>
        <p>
          <strong>5.3.4</strong> Funds not allocated within an agreed timetable
          may be returned to investors pro rata or reallocated by Committee
          decision.
        </p>

        <hr className="legal-prose__rule" />

        <h2>6. Investment Sectors</h2>
        <p>Capital Funds may be allocated to projects in the following sectors:</p>
        <ul>
          <li>
            Mining &amp; Minerals (gemstones, precious metals, lithium, tantalum,
            niobium, titanium, rare earth elements, tin, copper, lead, zinc,
            tungsten, molybdenum, manganese, iron ore, gypsum, limestone, marble,
            dolomite, feldspar, mica, silica sand, heavy mineral sands)
          </li>
          <li>Oil &amp; Gas Exploration</li>
          <li>Coal</li>
          <li>
            Fisheries (tuna, billfish, mackerel, snapper, groupers, emperors,
            goat fish, sweet lips, sharks, rays)
          </li>
          <li>Livestock &amp; Value-Chain Processing</li>
          <li>
            Agriculture &amp; Horticulture (including medicinal plants,
            sustainable farming, and botanical products)
          </li>
          <li>Other approved sectors determined by the Committee</li>
        </ul>

        <hr className="legal-prose__rule" />

        <h2>7. Donor Privacy &amp; Data Protection</h2>
        <p>
          <strong>7.1</strong> We collect personal information (name, contact
          details, payment information) solely for the purpose of processing
          donations, communicating with you, and administering the voting
          process.
        </p>
        <p>
          <strong>7.2</strong> We do not share, sell, or trade your personal
          data with third parties for marketing purposes.
        </p>
        <p>
          <strong>7.3</strong> Your information may be shared with:
        </p>
        <ul>
          <li>Payment processors to complete transactions</li>
          <li>The Committee and auditors for verification purposes</li>
          <li>Regulatory authorities if required by law</li>
        </ul>
        <p>
          <strong>7.4</strong> We take reasonable technical and organizational
          measures to protect your data.
        </p>
        <p>
          <strong>7.5</strong> You have the right to request access, correction,
          or deletion of your data by contacting us at the details below.
        </p>

        <hr className="legal-prose__rule" />

        <h2>8. Intellectual Property</h2>
        <p>
          <strong>8.1</strong> All content on this website, including text,
          graphics, logos, and images, is the property of Maalin iyo Habeen
          unless otherwise stated.
        </p>
        <p>
          <strong>8.2</strong> You may not reproduce, distribute, or use our
          content for commercial purposes without prior written consent.
        </p>
        <p>
          <strong>8.3</strong> You may share our content for non-commercial,
          informational purposes provided you attribute the source.
        </p>

        <hr className="legal-prose__rule" />

        <h2>9. Disclaimer &amp; Limitation of Liability</h2>
        <p>
          <strong>9.1</strong> Our services are provided &ldquo;as is&rdquo;
          without any warranties, express or implied.
        </p>
        <p>
          <strong>9.2</strong> We do not guarantee that:
        </p>
        <ul>
          <li>The website will be uninterrupted or error-free</li>
          <li>Investment returns will be generated</li>
          <li>Any specific project will succeed</li>
        </ul>
        <p>
          <strong>9.3</strong> Investment involves risk. The Capital Fund is not
          a guaranteed return vehicle. Past performance does not indicate future
          results.
        </p>
        <p>
          <strong>9.4</strong> To the fullest extent permitted by law, Maalin
          iyo Habeen shall not be liable for:
        </p>
        <ul>
          <li>Any direct, indirect, incidental, or consequential damages</li>
          <li>Loss of profits or investments</li>
          <li>Third-party claims</li>
          <li>
            Any loss arising from the allocation or non-allocation of Capital
            Funds
          </li>
        </ul>
        <p>
          <strong>9.5</strong> Our total liability shall not exceed the amount of
          your donation.
        </p>

        <hr className="legal-prose__rule" />

        <h2>10. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Maalin iyo Habeen, its
          directors, staff, volunteers, and Committee members from any claims,
          losses, liabilities, damages, or expenses arising out of:
        </p>
        <ul>
          <li>Your use of our website or services</li>
          <li>Your violation of these Terms</li>
          <li>Your participation in the voting or investment process</li>
          <li>Any dispute between you and other participants</li>
        </ul>

        <hr className="legal-prose__rule" />

        <h2>11. Modifications to Terms</h2>
        <p>
          <strong>11.1</strong> We reserve the right to update or modify these
          Terms at any time without prior notice.
        </p>
        <p>
          <strong>11.2</strong> Changes will be effective immediately upon
          posting to the website.
        </p>
        <p>
          <strong>11.3</strong> Your continued use of our services constitutes
          acceptance of the updated Terms.
        </p>
        <p>
          <strong>11.4</strong> Material changes (such as changes to the donation
          split or voting rules) will be communicated via email to registered
          donors where possible.
        </p>

        <hr className="legal-prose__rule" />

        <h2>12. Governing Law &amp; Dispute Resolution</h2>
        <p>
          <strong>12.1</strong> These Terms are governed by the laws of the
          Republic of Somaliland.
        </p>
        <p>
          <strong>12.2</strong> Any dispute arising out of or relating to these
          Terms shall first be referred to mediation by the Committee or a
          mutually agreed mediator.
        </p>
        <p>
          <strong>12.3</strong> If mediation fails, disputes shall be resolved by
          the courts of Somaliland having jurisdiction.
        </p>
        <p>
          <strong>12.4</strong> International donors agree to submit to the
          jurisdiction of Somaliland courts.
        </p>

        <hr className="legal-prose__rule" />

        <h2>13. Suspension &amp; Termination</h2>
        <p>
          <strong>13.1</strong> We reserve the right to suspend or terminate your
          access to our services if you:
        </p>
        <ul>
          <li>Violate these Terms</li>
          <li>Provide false or misleading information</li>
          <li>Engage in fraudulent or unlawful activity</li>
          <li>Interfere with the voting or escrow process</li>
        </ul>
        <p>
          <strong>13.2</strong> Suspension or termination does not affect any
          accrued rights or obligations.
        </p>

        <hr className="legal-prose__rule" />

        <h2>14. Severability</h2>
        <p>
          If any provision of these Terms is held to be invalid or unenforceable,
          the remaining provisions shall continue in full force and effect.
        </p>

        <hr className="legal-prose__rule" />

        <h2>15. Entire Agreement</h2>
        <p>
          These Terms constitute the entire agreement between you and Maalin iyo
          Habeen regarding your use of our services and supersede all prior
          agreements.
        </p>

        <hr className="legal-prose__rule" />

        <h2>16. Contact Us</h2>
        <p>
          For any questions, refund requests, or concerns regarding these Terms:
        </p>
        <p className="legal-prose__address">
          Maalin iyo Habeen
          <br />
          Republic of Somaliland
          <br />
          Email: {t('contact.emailValue')}
          <br />
          Phone: {t('contact.phoneValue')}
          <br />
          Address: {t('contact.addressValue')}
        </p>

        <hr className="legal-prose__rule" />

        <p className="legal-prose__closing">
          By using our website or making a donation, you acknowledge that you
          have read, understood, and agree to these Terms &amp; Conditions.
        </p>

        <p className="legal-prose__back">
          <Link to="/">&larr; {back}</Link>
        </p>
      </article>
    </main>
  );
}
